import { Typography, List, ListItem, ListItemIcon, ListItemText, Chip } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { useNavigate } from 'react-router-dom'
import { Card, Button } from '@/components/common'
import { PricingTier } from '@/types'

/**
 * Pricing card props
 */
export interface PricingCardProps {
  /** Pricing tier data */
  tier: PricingTier
}

/**
 * PricingCard Component
 * Displays a single pricing tier
 */
export const PricingCard = ({ tier }: PricingCardProps) => {
  const navigate = useNavigate()

  const handleCTAClick = () => {
    navigate('/contact')
  }

  return (
    <Card
      elevation={tier.isPopular ? 3 : 1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 3,
        position: 'relative',
        border: tier.isPopular ? '2px solid' : '1px solid',
        borderColor: tier.isPopular ? 'primary.main' : 'grey.200',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: (theme) => theme.shadows[tier.isPopular ? 8 : 4],
        },
      }}
    >
      {/* Popular Badge */}
      {tier.isPopular && (
        <Chip
          label="Most Popular"
          color="primary"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            fontWeight: 600,
          }}
        />
      )}

      {/* Tier Name */}
      <Typography
        variant="h5"
        component="h3"
        sx={{
          fontWeight: 700,
          mb: 1,
          textAlign: 'center',
          color: tier.isPopular ? 'primary.main' : 'text.primary',
        }}
      >
        {tier.name}
      </Typography>

      {/* Price */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mb: 3,
          textAlign: 'center',
          color: 'text.secondary',
        }}
      >
        {tier.priceLabel}
      </Typography>

      {/* Features List */}
      <List dense sx={{ mb: 3, flexGrow: 1 }}>
        {tier.features.map((feature, index) => (
          <ListItem key={index} disablePadding sx={{ mb: 1 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={feature}
              primaryTypographyProps={{
                variant: 'body2',
                color: 'text.secondary',
              }}
            />
          </ListItem>
        ))}
      </List>

      {/* CTA Button */}
      <Button
        variant={tier.isPopular ? 'primary' : 'outline'}
        fullWidth
        size="large"
        onClick={handleCTAClick}
      >
        {tier.ctaText}
      </Button>
    </Card>
  )
}
